import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardTitle } from '../ui/card';
import { WarningBanner, ConnectorLimitBanner } from '../ui/warning-banner';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { Plus, Plug, Loader2, AlertCircle, Crown } from 'lucide-react';
import { StoreCard } from './StoreCard';
import { StoreForm } from './StoreForm';
import { Store, storeService, notifyConnectorsChanged } from '../../services/store';
import { useAuth } from '../../contexts/AuthContext';
import { useUnifiedSubscription } from '../../contexts/SubscriptionContext';
import { useOrganization } from '../../contexts/OrganizationContext';

export function StoreList() {
  const { subscription, isLoading: isSubscriptionLoading } = useUnifiedSubscription();
  const { hasPermission } = useOrganization();
  const [stores, setStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingStore, setEditingStore] = useState<Store | null>(null);
  const [storeToDelete, setStoreToDelete] = useState<Store | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await storeService.getStores();
      setStores(data);
    } catch (error: any) {
      setError(error.message || 'Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  // Check if user can add more stores
  const canAddStore = () => {
    if (!hasPermission('stores', 'create')) return false;
    if (!subscription) return false;
    const storeLimit = subscription.store_limit || 0;
    return stores.length < storeLimit;
  };

  const getStoreLimit = () => {
    const limit = subscription?.store_limit || 0;
    return limit;
  };

  const getRemainingStores = () => {
    const limit = getStoreLimit();
    // Show "Unlimited" for plans with 999999 store limit
    if (limit >= 999999) return '∞';
    return Math.max(0, limit - stores.length);
  };

  const handleAddStore = () => {
    if (!canAddStore()) {
      return; // Prevent adding store if at limit
    }
    setEditingStore(null);
    setShowForm(true);
  };

  const handleEditStore = (store: Store) => {
    setEditingStore(store);
    setShowForm(true);
  };

  const handleDeleteStore = (store: Store) => {
    setStoreToDelete(store);
  };

  const confirmDeleteStore = async () => {
    if (!storeToDelete) return;

    try {
      setIsDeleting(true);
      await storeService.deleteStore(storeToDelete.id);
      setStores(stores.filter(s => s.id !== storeToDelete.id));
      notifyConnectorsChanged(); // Update sidebar menu
      setStoreToDelete(null);
    } catch (error: any) {
      setError(error.message || 'Failed to delete connector');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStoreFormSuccess = (store: Store) => {
    if (editingStore) {
      // Update existing store
      setStores(stores.map(s => s.id === store.id ? store : s));
    } else {
      // Add new store
      setStores([...stores, store]);
      notifyConnectorsChanged(); // Update sidebar menu for new connector
    }
    setShowForm(false);
    setEditingStore(null);
  };

  const handleStoreUpdate = (updatedStore: Store) => {
    setStores(stores.map(s => s.id === updatedStore.id ? updatedStore : s));
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingStore(null);
  };

  if (loading) {
    return (
      <div className="space-y-8 animate-fade-in">
        {/* Header skeleton */}
        <div className="flex items-center justify-between animate-pulse">
          <div className="space-y-2">
            <div className="h-8 bg-muted rounded w-64"></div>
            <div className="h-4 bg-muted rounded w-96"></div>
          </div>
          <div className="h-10 bg-muted rounded w-32"></div>
        </div>
        
        {/* Loading cards */}
        <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-2">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="h-48 bg-muted rounded-xl"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (showForm) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={handleFormCancel}
            className="mb-4"
          >
            ← Back to Connectors
          </Button>
        </div>
        <StoreForm
          store={editingStore || undefined}
          onSuccess={handleStoreFormSuccess}
          onCancel={handleFormCancel}
        />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex items-center justify-between animate-slide-down">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight">Connector Management</h1>
          <p className="text-muted-foreground">
            Connect Magento stores or upload images directly for processing
          </p>
        </div>
        {isSubscriptionLoading ? (
          <div className="h-9 w-32 bg-muted rounded animate-pulse"></div>
        ) : subscription && (
          <>
            {!hasPermission('stores', 'create') ? (
              <Button
                disabled
                className="gap-2 opacity-50 cursor-not-allowed"
                title="You don't have permission to create connectors"
              >
                <Plus className="w-4 h-4" />
                No Permission
              </Button>
            ) : canAddStore() ? (
              <Button
                onClick={handleAddStore}
                className="gap-2 shadow-lg hover:shadow-xl hover:scale-105 hover:-translate-y-0.5 transition-all duration-300 group"
              >
                <Plus className="w-4 h-4 group-hover:rotate-90 transition-transform duration-300" />
                Add Connector{getStoreLimit() < 999999 && ` (${getRemainingStores()} remaining)`}
              </Button>
            ) : (
              <Button
                disabled
                className="gap-2 opacity-50 cursor-not-allowed"
                title="Connector limit reached. Upgrade to add more."
              >
                <Crown className="w-4 h-4" />
                Upgrade to Add More
              </Button>
            )}
          </>
        )}
      </div>

      {error && (
        <WarningBanner
          variant="error"
          title="Error"
          message={error}
          dismissible
          onDismiss={() => setError(null)}
        />
      )}

      {subscription && subscription.status !== 'canceled' && stores.length >= getStoreLimit() && (
        <ConnectorLimitBanner
          currentConnectors={stores.length}
          maxConnectors={getStoreLimit()}
          planName={subscription.plan_name}
          onUpgrade={() => {
            // Navigate to billing page
            window.location.href = '/billing';
          }}
        />
      )}

      {stores.length === 0 ? (
        <Card className="card-modern border-2 border-primary/20 bg-gradient-to-br from-primary/5 via-background to-accent/5 animate-scale-in">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="relative mb-6">
              <Plug className="h-16 w-16 text-primary animate-float" />
              <div className="absolute inset-0 bg-primary/20 rounded-full animate-ping"></div>
            </div>
            <CardTitle className="text-2xl mb-3 text-center">No Connectors Added</CardTitle>
            <CardDescription className="text-center mb-8 max-w-md text-lg">
              Add a connector to start processing images. Connect a Magento store
              or use the upload connector for direct image processing.
            </CardDescription>
            {subscription && (
              <>
                {canAddStore() ? (
                  <Button
                    onClick={handleAddStore}
                    className="gap-2 shadow-lg hover:shadow-xl hover:scale-105 hover:-translate-y-1 transition-all duration-300 group"
                    size="lg"
                  >
                    <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" />
                    Add Your First Connector
                  </Button>
                ) : (
                  <Button
                    disabled
                    className="gap-2 opacity-50 cursor-not-allowed"
                    size="lg"
                    title="Connector limit reached. Upgrade to add connectors."
                  >
                    <Crown className="w-5 h-5" />
                    Upgrade to Add Connectors
                  </Button>
                )}
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-2">
          {stores.map((store, index) => (
            <div
              key={store.id}
              className="animate-slide-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <StoreCard
                store={store}
                onEdit={handleEditStore}
                onDelete={handleDeleteStore}
                onUpdate={handleStoreUpdate}
              />
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!storeToDelete}
        onOpenChange={(open) => !open && setStoreToDelete(null)}
        title="Delete Connector"
        description={`Are you sure you want to delete "${storeToDelete?.name}"? This will remove the connector and all associated data. This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDeleteStore}
        variant="destructive"
        isLoading={isDeleting}
      />

    </div>
  );
}